import { apiGet, apiPost } from '../../../adapters/api-client/api';

/**
 * UC-A03 — evidence and progress integrity, and class health (ADM-13).
 *
 * Every payload is a projection the backend computed from the authoritative
 * records. The console shows it and links back; it never edits evidence, and
 * the only action it can take is asking a teacher for the missing piece.
 */

export type IntegrityCheckType =
  | 'MILESTONE_VS_PAYMENT'
  | 'SETTLEMENT_VS_TEACHER_VIEW'
  | 'HOLDBACK_VS_DISPUTE'
  | 'BOOKING_VS_MILESTONE'
  | 'EVIDENCE_VS_STATUS'
  | 'RECORDING_VS_COMPLETION'
  | 'ENROLLMENT_VS_MILESTONES'
  | 'RELEASE_VS_EVIDENCE'
  | 'RUBRIC_AFTER_RELEASE'
  | 'FORMAL_RESULT_VS_PROFILE'
  | 'PARENT_PROGRESS_AUDIT';

export interface IntegrityRun {
  checkType: IntegrityCheckType;
  runId?: string;
  completedAt?: string;
  enrollmentsScanned?: number;
  status?: 'ok' | 'mismatch' | 'error';
  mismatches?: Array<{ enrollmentId: string; reason: string; sequence?: number }>;
  errorMessage?: string;
}

export interface IntegrityFinding {
  checkType: IntegrityCheckType;
  reason: string;
  finding?: string;
  detail: string;
}

export interface IntegrityBreak {
  enrollmentId: string;
  subjectKey: string;
  milestoneId?: string;
  sequence?: number;
  teacherId: string;
  courseId: string;
  status: 'OPEN' | 'RESOLVED';
  findings: IntegrityFinding[];
  firstDetectedAt: string;
  lastDetectedAt: string;
  holdPlaced: boolean;
  evidenceRequestedAt?: string;
  evidenceDueAt?: string;
  evidenceDeadlineHours?: number;
  evidenceRequestCount?: number;
  escalatedAt?: string;
  deadlineState?: 'OPEN' | 'EXPIRED' | null;
}

export interface EvidenceHold {
  enrollmentId: string;
  milestoneId: string;
  sequence?: number;
  reasons: string[];
  placedAt: string;
}

export interface IntegrityOverview {
  generatedAt: string;
  latestRuns: IntegrityRun[];
  breaks: IntegrityBreak[];
  holds: EvidenceHold[];
  evidenceDeadline: { hours: number; parameterVersion: string };
}

export interface EvidenceObligation {
  type: string;
  applicability: 'REQUIRED' | 'N_A';
  /** PENDING: the provider is still processing (a recording), not yet a break. */
  state: 'COMPLETE' | 'PENDING' | 'MISSING' | 'AMBIGUOUS' | 'N_A';
  providerDelay?: boolean;
  /** RECORDING only: processing past the governed stale threshold. */
  stale?: boolean;
}

export interface MilestoneEvidence {
  milestoneId: string;
  sequence: number;
  type: string;
  status: string;
  bookingState?: string;
  profile: { level: number | null; ref: string | null; source: string };
  facts: {
    teacherCheckedInAt?: string;
    studentCheckedInAt?: string;
    learnerAttended: boolean;
    durationMinutes?: number;
    recordingPresent: boolean;
    recordingStatus?: string;
    rubricPresent: boolean;
    rubricSubmittedAt?: string;
    completedAt?: string;
    releasedAt?: string;
  };
  /** MC-08 §3 — sessions only: connection, association, identity and attendance, apart. */
  presence?: PresenceDimensions;
  obligations: EvidenceObligation[];
  integrityStatus: 'COMPLETE' | 'PENDING' | 'INCOMPLETE' | 'AMBIGUOUS' | 'EXCEPTION_APPROVED';
  hold: { placedAt: string; reasons: string[]; correlationId?: string } | null;
  integrityBreak: IntegrityBreak | null;
  deadline: { dueAt: string; state: 'OPEN' | 'EXPIRED'; hours?: number } | null;
  /** ADM-03-FR-01 — the source versions these facts were read from. */
  sourceVersions?: { deliveryFacts: { ref: string; asOf?: string }; rubricVersion?: number };
}

/** ADM-03-FR-01 — what the read model was built on, and the read's correlation id. */
export interface EvidenceLineage {
  correlationId: string;
  learnerRef: string;
  enrollmentRef: string;
  teacherRef: string;
  courseVersionRef: string | null;
  learningPolicyVersion: string;
  obligationPolicy: {
    minDeliveryMinutes: number;
    recordingStaleMinutes: number;
    parameterVersion: string;
  };
  progressProjection: {
    ref: string;
    projectionVersion: number;
    policyVersion: string;
    asOf: string;
    dataSufficiency: string;
  } | null;
  integrityRunIds: string[];
}

export interface EvidenceDrillDown {
  enrollmentId: string;
  courseId: string;
  teacherId: string;
  state: string;
  milestones: MilestoneEvidence[];
  enrollmentBreak: IntegrityBreak | null;
  supportCaseRoute: string;
  lineage?: EvidenceLineage;
}

/** ADM-03-FR-08 — one rubric version: who, when and why. Never the scores or the text. */
export interface RubricHistoryVersion {
  version: number;
  priorVersion?: number;
  slot: 'SESSION' | 'FINAL';
  actorId: string;
  recordedAt: string;
  correctionReason?: string;
  learningProfile: string;
  policyVersion: string;
  courseVersionId?: string;
  afterRelease: boolean;
}

export interface RubricHistory {
  enrollmentId: string;
  milestoneId: string;
  sequence: number;
  /** OPTIONAL where a rubric may exist; never a payment condition (MC-01 v1-7). */
  applicability: 'OPTIONAL' | 'N_A';
  releasedAt?: string;
  versions: RubricHistoryVersion[];
  correlationId: string;
}

export interface RatioMetric {
  numerator: number;
  denominator: number;
  value: number | null;
  status: 'MEASURED' | 'N_A';
}

export interface RatingDimension {
  averageRating: number | null;
  totalReviews: number;
  scale: '1-5';
}

export interface PresenceDimensions {
  connection: {
    status: 'CONNECTED' | 'NEVER_CONNECTED' | 'PENDING' | 'UNKNOWN';
    firstJoinedAt?: string;
    lastLeftAt?: string;
    connectedSeconds: number;
  };
  association: { unassociatedAttendees: number; review: boolean };
  identity: { status: 'NOT_VERIFIED'; openGaps: string[] };
  attendance: {
    outcome?: 'PRESENT' | 'LATE' | 'NO_SHOW' | 'UNKNOWN';
    fullAttendance: 'PENDING_GOVERNED_THRESHOLD';
  };
}

/** MC-06 — a ratio with its parts; an empty denominator is a named state, never 0 %. */
export interface QualityRatio {
  numerator: number;
  denominator: number;
  value: number | null;
  status: 'MEASURED' | 'NO_ELIGIBLE_CYCLES' | 'NO_RESPONSES';
}

/** ADM-03-FR-09 — which definition, version and window a number was computed under. */
export interface DefinitionStamp {
  id: string;
  version: string;
  window: {
    kind: 'COHORT_TO_DATE' | 'TERMINATION_MONTH' | 'ROLLING_DAYS';
    timeZone?: string;
    /** ROLLING_DAYS only: how many days back from `to`. */
    days?: number;
    from?: string;
    to: string;
  };
}

export type SatisfactionState =
  | 'FAVORABLE'
  | 'UNFAVORABLE'
  | 'NO_RESPONSE'
  | 'WINDOW_OPEN'
  | 'SURVEY_FAILED'
  | 'NOT_OPENED';

export interface ConfirmedServiceQuality {
  status: 'FINAL' | 'PROVISIONAL' | 'INCOMPLETE';
  definition: DefinitionStamp;
  period: string;
  reportingTimeZone: string;
  cutoff: string;
  revision: string;
  incidents: Array<{
    incident:
      | 'SURVEY_NOT_OPENED'
      | 'SURVEY_FAILED'
      | 'EVIDENCE_INTEGRATION_FAILURE'
      | 'CANCELLATION_ACTOR_UNRECORDED';
    cycles: number;
  }>;
  indicators: {
    confirmedQuality: QualityRatio;
    serviceCompleted: QualityRatio;
    serviceWithEvidence: QualityRatio;
    favorableAmongResponses: QualityRatio;
    responseCoverage: QualityRatio;
  };
  breakdown: {
    completedWithoutEvidence: Record<
      'MISSING' | 'INVALID' | 'PENDING_VALIDATION' | 'INTEGRATION_FAILURE',
      number
    >;
    notCompleted: Record<
      | 'SESSION_NOT_DELIVERED'
      | 'CANCELLED_BY_PLATFORM'
      | 'CANCELLED_BY_FAMILY'
      | 'CANCELLED_ACTOR_UNRECORDED',
      number
    >;
    satisfaction: Record<SatisfactionState, number>;
  };
  excluded: Record<
    | 'NEVER_CONFIRMED'
    | 'NO_VERIFIABLE_CYCLE'
    | 'FAMILY_CANCELLED_BEFORE_START'
    | 'CANCELLATION_ACTOR_UNRECORDED',
    number
  >;
  inProgress: { total: number; affected: number };
}

/** `INACTIVE` — MC-06 is dark until A09 is disposed (DEC-S3-A03-07): nothing to show at all. */
export type ServiceQualityView =
  | ConfirmedServiceQuality
  | { status: 'SOURCE_UNAVAILABLE'; source: 'SATISFACTION_SURVEYS'; period: string }
  | { status: 'INACTIVE'; period: string };

export type ClassServiceQuality =
  | {
      status: 'MEASURED';
      definitionId: string;
      definitionVersion: string;
      period: string;
      resultStatus: ConfirmedServiceQuality['status'];
      confirmedQuality: QualityRatio;
      responseCoverage: QualityRatio;
    }
  | { status: 'SOURCE_UNAVAILABLE' }
  | { status: 'INACTIVE' };

export interface ClassHealth {
  courseId: string;
  courseTitle?: string;
  teacherId?: string;
  enrollments: number;
  cycleCompletion: RatioMetric;
  attendance: RatioMetric;
  rubricCompletion: RatioMetric & { notApplicable: number };
  evidenceCompliance: RatioMetric & { notApplicable: number };
  progressIntegrity: { openBreaks: number; alert: boolean };
  reviews: { course: RatingDimension; teacher: RatingDimension };
  classQuality: ClassServiceQuality;
  excluded: Array<{ requirement: string; reason: string }>;
}

export const getIntegrityOverview = () =>
  apiGet<IntegrityOverview>('/api/v1/admin/integrity/overview');

export interface ClassHealthView {
  generatedAt: string;
  definitions: Record<
    'cycleCompletion' | 'attendance' | 'rubricCompletion' | 'evidenceCompliance',
    DefinitionStamp
  >;
  serviceQuality: ServiceQualityView;
  classes: ClassHealth[];
}

/** `period` is a calendar month, `YYYY-MM`; the server defaults to the current one. */
export const getClassHealth = (period?: string) =>
  apiGet<ClassHealthView>(
    `/api/v1/admin/integrity/class-health${period ? `?period=${encodeURIComponent(period)}` : ''}`,
  );

/**
 * ADM-03-FR-04 — audited: the server refuses a drill-down without a stated
 * reason. A POST so the reason stays out of URLs and logs (DEC-S3-A03-12).
 */
export const getEnrollmentEvidence = (enrollmentId: string, reason: string) =>
  apiPost<EvidenceDrillDown>(
    `/api/v1/admin/integrity/evidence/${encodeURIComponent(enrollmentId)}`,
    { reason },
  );

/** ADM-03-FR-08 — a milestone's rubric version history. Audited, read-only. */
export const getRubricHistory = (enrollmentId: string, sequence: number, reason: string) =>
  apiPost<RubricHistory>(
    `/api/v1/admin/integrity/evidence/${encodeURIComponent(
      enrollmentId,
    )}/milestones/${sequence}/rubric-history`,
    { reason },
  );

export const requestEvidence = (enrollmentId: string, milestoneId: string) =>
  apiPost<{ evidenceRequestedAt: string; evidenceDueAt: string; evidenceRequestCount: number }>(
    `/api/v1/admin/integrity/evidence/${encodeURIComponent(enrollmentId)}/${encodeURIComponent(
      milestoneId,
    )}/request`,
    {},
  );

/** The S5.3 trigger. Needs MOVE_MONEY: what a run reports can release a held session. */
export const runIntegrityChecks = () =>
  apiPost<{ runs: IntegrityRun[] }>('/api/v1/admin/reliability/integrity/run', {});
